import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular-boost';
import * as Enumerable from 'linq';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { LosAuthV2Model } from '../models/los-auth-v2.model';
import { LosLoginResponseV2Model } from '../models/los-login-response-v2.model';
import { LosLoginV2Model } from '../models/los-login-v2.model';
import { LosMenuV2Model } from '../models/los-menu-v2.model';
import { LosRoomV2Model } from '../models/los-room-v2.model';
import { LosUserChangePassword } from '../models/los-user-change-password.model';
import { LosMenusV2Service } from './los-menus-v2.service';
import { LosRoomsV2Service } from './los-rooms-v2.service';

@Injectable({
  providedIn: 'root'
})
export class LosAuthV2Service {

  private authStorage = 'los-auth';
  private roomStorage = 'los-room';

  constructor(
    private http: HttpClient,
    private losMenusV2Service: LosMenusV2Service,
    private losRoomsV2Service: LosRoomsV2Service,
    private apollo: Apollo
  ) { }

  login(data: LosLoginV2Model): Observable<LosLoginResponseV2Model> {
    // tslint:disable-next-line:max-line-length
    const body = encodeURI(`grant_type=password&username=${data.username}&password=${data.password}&client_id=${data.clientId}&client_secret=123`);

    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
    };

    return this.http.post<LosLoginResponseV2Model>(data.url, body, options).pipe(map(response => {
      sessionStorage.setItem(this.authStorage, JSON.stringify(response));
      return response;
    }));
  }

  logout(): boolean {
    sessionStorage.removeItem(this.authStorage);
    sessionStorage.removeItem(this.roomStorage);
    return true;
  }

  logoutSubscriber(): Observable<boolean> {
    return new Observable(subscribe => {
      sessionStorage.removeItem(this.authStorage);
      sessionStorage.removeItem(this.roomStorage);
      subscribe.next(true);
      subscribe.complete();
    });
  }

  isLogged(): boolean {
    return sessionStorage.getItem(this.authStorage) !== null;
  }

  isLoggedSubscriber(): Observable<boolean> {
    return new Observable(subscriber => {
      subscriber.next(sessionStorage.getItem(this.authStorage) !== null);
      subscriber.complete();
    });
  }

  getUser(): LosAuthV2Model {
    if (sessionStorage.getItem(this.authStorage) !== null) {
      return this.decodeToken(JSON.parse(sessionStorage.getItem(this.authStorage)).access_token);
    }
  }

  getUserSubscriber(): Observable<LosAuthV2Model> {
    return new Observable(subscriber => {
      if (sessionStorage.getItem(this.authStorage) !== null) {
        subscriber.next(this.decodeToken(JSON.parse(sessionStorage.getItem(this.authStorage)).access_token));
      } else {
        subscriber.next(null);
      }
      subscriber.complete();
    });
  }

  getToken(): string {
    if (sessionStorage.getItem(this.authStorage) !== null) {
      return JSON.parse(sessionStorage.getItem(this.authStorage)).access_token;
    }
  }

  getUserRoles(info = false): string[] {
    const user = this.getUser();
    if (user) {
      if (!info) {
        return user.role;
      } else {

      }
    }
  }

  getUserRolesSubscriber(info = false): Observable<string[]> {
    return new Observable(subscriber => {
      return this.getUserSubscriber().subscribe(user => {
        if (user) {
          if (!info) {
            subscriber.next(user.role);
          }
        }
        subscriber.complete();
      });
    });
  }

  getUserActions(info = false): string[] {
    const user = this.getUser();
    if (user) {
      if (!info) {
        return user.actions;
      }
    }
  }

  getUserActionsSubscriber(info = false): Observable<string[]> {
    return new Observable(subscriber => {
      return this.getUserSubscriber().subscribe(user => {
        if (user) {
          if (!info) {
            subscriber.next(user.actions);
          }
        }
        subscriber.complete();
      });
    });
  }

  getUserMenus(): string[] {
    const user = this.getUser();
    if (user) {
      return user.menus;
    }
  }

  getUserMenusSubscriber(): Observable<LosMenuV2Model[]> {
    return new Observable(subscriber => {
      return this.getUserSubscriber().subscribe(user => {
        if (user) {
          return this.losMenusV2Service.getMenus(user.menus).subscribe(menus => {
            subscriber.next(menus);
          });
        }
        subscriber.complete();
      });
    });
  }

  hasRolePermission(roles: string[]): boolean {
    let hasPermission = false;
    const userRoles = this.getUserRoles();

    if (roles) {
      roles.forEach((role) => {
        if (userRoles && userRoles.indexOf(role.toString()) > -1) {
          hasPermission = true;
        }
      });
    } else {
      hasPermission = true;
    }

    return hasPermission;
  }

  hasRolePermissionSubscriber(roles: string[]): Observable<boolean> {
    return new Observable(subscribe => {
      return this.getUserRolesSubscriber().subscribe(userRoles => {
        let hasPermission = false;

        if (roles) {
          roles.forEach((role) => {
            if (userRoles && userRoles.indexOf(role.toString()) > -1) {
              hasPermission = true;
            }
          });
        } else {
          hasPermission = true;
        }

        subscribe.next(hasPermission);
        subscribe.complete();
      });
    });
  }

  hasActionPermission(actions: string[]): boolean {
    let hasPermission = false;
    const userActions = this.getUserActions();

    if (actions) {
      actions.forEach((action) => {
        if (userActions && userActions.indexOf(action.toString()) > -1) {
          hasPermission = true;
        }
      });
    } else {
      hasPermission = true;
    }

    return hasPermission;
  }

  hasActionPermissionSubscriber(actions: string[]): Observable<boolean> {
    return new Observable(subscribe => {
      return this.getUserActionsSubscriber().subscribe(userActions => {
        let hasPermission = false;

        if (actions) {
          actions.forEach((action) => {
            if (userActions && userActions.indexOf(action.toString()) > -1) {
              hasPermission = true;
            }
          });
        } else {
          hasPermission = true;
        }

        subscribe.next(hasPermission);
        subscribe.complete();
      });
    });
  }

  hasMenuPermission(menus: string[]): boolean {
    let hasPermission = false;
    const userMenus = this.getUserMenus();

    if (menus) {
      menus.forEach((menu) => {
        if (userMenus && userMenus.indexOf(menu.toString()) > -1) {
          hasPermission = true;
        }
      });
    } else {
      hasPermission = true;
    }

    return hasPermission;
  }

  hasMenuPermissionSubscriber(menus: string[]): Observable<boolean> {
    return new Observable(subscribe => {
      return this.getUserMenusSubscriber().subscribe(userMenus => {
        let hasPermission = false;

        if (menus) {
          menus.forEach((menu) => {
            if (userMenus && Enumerable.from<LosMenuV2Model>(userMenus).any(x => x._id.toString() === menu)) {
              hasPermission = true;
            }
          });
        } else {
          hasPermission = true;
        }

        subscribe.next(hasPermission);
        subscribe.complete();
      });
    });
  }

  getApplicationsSubscriber(): Subscription {
    return this.getUserMenusSubscriber().subscribe(menus => {
      return Enumerable.from(menus)
        .where(x => x.application !== null)
        .groupBy(x => x.application._id)
        .select(x => {
          return {
            key: x.key(),
            application: x.getSource()[0].application,
            source: x.getSource()
          };
        })
        .toArray();
    });
  }

  getRooms(info = false): string[] {
    const user = this.getUser();
    if (user) {
      if (!info) {
        return user.rooms;
      }
    }
  }

  getRoomsSubscriber(): Observable<LosRoomV2Model[]> {
    return new Observable(subscriber => {
      return this.getUserSubscriber().subscribe(user => {
        if (user) {
          return this.losRoomsV2Service.getRooms(user.rooms).subscribe(rooms => {
            subscriber.next(rooms);
          });
        }
        subscriber.complete();
      });
    });
  }

  getRoomDefault(): string {
    const auth = this.getUser();
    if (auth) {
      return auth.room;
    }
  }

  getRoomDefaultSubscriber(): Observable<string> {
    return new Observable(subscriber => {
      return this.getUserSubscriber().subscribe(auth => {
        if (auth) {
          subscriber.next(auth.room);
        }
        subscriber.complete();
      });
    });
  }

  setRoomSubscriber(room: LosRoomV2Model): Observable<boolean> {
    return new Observable(subscriber => {
      sessionStorage.setItem(this.roomStorage, JSON.stringify(room));
      subscriber.next(true);
      subscriber.complete();
    });
  }

  getRoom(): LosRoomV2Model {
    return JSON.parse(sessionStorage.getItem(this.roomStorage) as string) as LosRoomV2Model;
  }

  getRoomSubscriber(): Observable<LosRoomV2Model> {
    return new Observable(subscriber => {
      subscriber.next(JSON.parse(sessionStorage.getItem(this.roomStorage) as string) as LosRoomV2Model);
      subscriber.complete();
    });
  }

  userChangePassword(data: LosUserChangePassword) {
    return this.apollo.use('public').mutate({
      mutation: gql`
      mutation userChangePassword (
        $id: String!
        $passwordCurrent: String!
        $newPassword: String!
        $confirmPassword: String!
      ) {
        userChangePassword (
          id: $id
          passwordCurrent: $passwordCurrent
          newPassword: $newPassword
          confirmPassword: $confirmPassword
        ) {
          user {
            _id
            username
            userdomain
            usersap
            email
          }
        }
      }
      `,
      variables: {
        id: data.id,
        passwordCurrent: data.passwordCurrent,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword
      }
    });
  }

  private decodeToken(token): LosAuthV2Model {
    return JSON.parse(window.atob(token.split('.')[1].replace('-', '+').replace('_', '/')));
  }
}
